import { Dialog, DialogContent } from "@mui/material";
import * as api from "api/Knowledge";
import ButtonCardAction from "components/Buttons/ButtonCardAction";
import IconSociaDislikes from "components/Icons/IconSociaDislikes";
import IconSocialComments from "components/Icons/IconSocialComments";
import IconSocialLikes from "components/Icons/IconSocialLikes";
import IconSocialThanks from "components/Icons/IconSocialThanks";
import { MuiRegularButtonColors } from 'components/MuiExtras/Button/MuiRegularButton';
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { PublicationLikeOption } from "models/ApiEnums";
import { FeedItemDto, FeedItemInteractionDto, FeedItemStatisticsDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import CommentBox from "views/Publication/Components/CommentBox";
import PublicationLikesReviewDrawer from "views/Timeline/Dialogs/PublicationLikesReviewDrawer";
import PublicationThanksReviewDrawer from "views/Timeline/Dialogs/PublicationThanksReviewDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
    updatingLike: boolean;
}

interface IProps {
    data: FeedItemDto;
    fullView: boolean;
    showCommentsButton: boolean;
    onUpdateCard: (data: DeepPartial<FeedItemDto>) => void;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class ActionsSocial extends React.PureComponent<Props, IState> {

    private handlePublicationId: string;
    private localInteraction: FeedItemInteractionDto;
    private localStatistics: FeedItemStatisticsDto;

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            updatingLike: false,
        };
    }

    ///
    private postLike(likeOption: PublicationLikeOption) {

        const { t } = this.props;

        this.setState({ updatingLike: true });

        api.postLike({ objectId: this.handlePublicationId, likeOption })
            .then((result) => {
                if (result) {
                    if (this.handlePublicationId != this.props.data.publicationId)
                        this.props.onUpdateCard({
                            publicationId: this.props.data.publicationId,
                            bestAnswer: {
                                statistics: result.statistics,
                                interaction: result.interaction,
                            }
                        });
                    else
                        this.props.onUpdateCard({
                            publicationId: this.props.data.publicationId,
                            statistics: result.statistics,
                            interaction: result.interaction,
                        });

                    this.setState({ updatingLike: false });
                }
                else {
                    this.props.snackbar.showMessage(t("Common.Feed.Messages.PostLikeFailed"), "error");
                }
            })
            .catch((error) => {
                if (!error.handled) {
                    this.props.snackbar.showMessage(t("Common.Feed.Messages.PostLikeFailed"), "error");
                }
                this.setState({ updatingLike: false });
            });
    }

    ///
    private handleCloseDialog(): void {
        this.setState({ dialog: null });
    }

    ///
    private handleLikeClick(likeOption: PublicationLikeOption) {
        this.postLike(likeOption);
    }

    ///
    private handleLikeReviewClick() {
        this.setState({
            dialog: <PublicationLikesReviewDrawer
                publicationId={this.handlePublicationId}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    }

    ///
    private handleThanksClick() {
        this.setState({
            dialog: <PublicationThanksReviewDrawer
                publicationId={this.handlePublicationId}
                canAddThanks={this.localInteraction.canThanks}
                onUpdate={
                    (feedItem) => {
                        if (this.handlePublicationId != this.props.data.publicationId)
                            this.props.onUpdateCard({
                                publicationId: this.props.data.publicationId,
                                bestAnswer: {
                                    statistics: feedItem.statistics,
                                    interaction: feedItem.interaction,
                                }
                            });
                        else
                            this.props.onUpdateCard({
                                publicationId: this.props.data.publicationId,
                                statistics: feedItem.statistics,
                                interaction: feedItem.interaction,
                            });
                    }
                }

                onClose={() => this.setState({ dialog: null })}
            />
        });
    }

    ///
    private renderComments() {
        const { t } = this.props;

        return <Dialog
            open
            fullWidth
            maxWidth="md"
        >
            <MuiDialogTitleClose onClose={() => this.handleCloseDialog()}>
                <span>{t("Common.Feed.Comment.BoxTitle")}</span>
            </MuiDialogTitleClose>

            <DialogContent>

                <CommentBox
                    publicationId={this.handlePublicationId}
                    autoFocus
                    overModal
                    onUpdateCommentCounter={
                        (comments) => {
                            if (this.handlePublicationId != this.props.data.publicationId)

                                this.props.onUpdateCard({
                                    publicationId: this.props.data.publicationId,
                                    bestAnswer: { statistics: { comments } }
                                });

                            else

                                this.props.onUpdateCard({
                                    publicationId: this.props.data.publicationId,
                                    statistics: { comments }
                                });
                        }
                    }
                />

            </DialogContent>
        </Dialog>;
    }

    ///
    public render() {

        const {
            t,
            showCommentsButton,
            data: {
                publicationId,
                interaction,
                statistics,
                bestAnswer
            },
            fullView
        } = this.props;

        this.handlePublicationId = fullView || bestAnswer == null ? publicationId : bestAnswer.publicationId;
        this.localInteraction = fullView || bestAnswer == null ? interaction : bestAnswer.interaction;
        this.localStatistics = fullView || bestAnswer == null ? statistics : bestAnswer.statistics;

        /**
         *  CUIDADO: Perguntas com melhor resposta associada => as ações da área SOCIAL devem se referir à RESPOSTA e não à pergunta.
         **/

        const likeColor: MuiRegularButtonColors =
            (this.state.updatingLike)
                ? this.localInteraction.likePosted != null ? "transparent" : "pressed"
                : this.localInteraction.likePosted != null ? "pressed" : "transparent";

        const thanksColor: MuiRegularButtonColors =
            this.localInteraction.thanked === true ? "pressed" : "transparent";

        return (
            <>
                <ButtonCardAction
                    title={(this.localInteraction.canLike) ? t("Common.Feed.Actions.ButtonLikeTitle") : t("Common.Feed.Actions.ButtonLikedTitle")}
                    disabled={this.state.updatingLike}
                    color={likeColor}
                    startIcon={this.localInteraction.likePosted == PublicationLikeOption.Dislike ? <IconSociaDislikes /> : <IconSocialLikes />}
                    onClick={
                        () => this.localInteraction.canLike
                            ? this.handleLikeClick(this.localInteraction.likePosted != null ? PublicationLikeOption.Neutral : PublicationLikeOption.Like)
                            : this.handleLikeReviewClick()
                    }
                >
                    <span>{this.localStatistics.likes - this.localStatistics.dislikes}</span>
                </ButtonCardAction>

                {
                    showCommentsButton &&
                    <ButtonCardAction
                        title={t("Common.Feed.Actions.ButtonCommentTitle")}
                        color={this.localStatistics.comments > 0 ? "pressed" : "transparent"}
                        startIcon={<IconSocialComments />}
                        onClick={() => this.setState({ dialog: this.renderComments() })}
                    >
                        <span>{this.localStatistics.comments}</span>
                    </ButtonCardAction>
                }

                {
                    this.localInteraction.publicationTypeAllowThanks &&
                    <ButtonCardAction
                        title={(this.localInteraction.canThanks) ? t("Common.Feed.Actions.ButtonThanksTitle") : t("Common.Feed.Actions.ButtonThankedTitle")}
                        color={thanksColor}
                        startIcon={<IconSocialThanks />}
                        onClick={() => this.handleThanksClick()}
                    >
                        <span>{this.localStatistics.thanks}</span>
                    </ButtonCardAction>
                }
                {this.state.dialog}
            </>
        );
    }
}

export default withSnackbar(withTranslation()(ActionsSocial));
